import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { userService } from '../services/userService';
import { brandService } from '../services/brandService';
import { customerService } from '../services/customerService';
import { permissionService, UserPermissions } from '../services/permissionService';
import { User, Brand, Customer } from '../types';

interface AppDataContextType {
  // Data
  users: User[];
  brands: Brand[];
  suppliers: Brand[];
  customers: Customer[];
  permissions: UserPermissions | null;
  
  // Loading states
  loadingUsers: boolean;
  loadingBrands: boolean;
  loadingCustomers: boolean;
  loadingPermissions: boolean;
  
  // Error states
  errorUsers: string | null;
  errorBrands: string | null;
  errorCustomers: string | null;
  errorPermissions: string | null;
  
  // Refresh methods
  refreshUsers: () => Promise<void>;
  refreshUsersForDropdown: () => Promise<void>; // Lightweight endpoint for dropdowns
  refreshBrands: () => Promise<void>;
  refreshSuppliers: () => Promise<void>;
  refreshCustomers: () => Promise<void>;
  refreshPermissions: () => Promise<UserPermissions | null>;
  refreshAll: () => Promise<void>;
  
  // Clear data
  clearData: () => void;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export const AppDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const hasInitializedRef = useRef(false);
  const lastUserRef = useRef<string | null>(null);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  
  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [suppliers, setSuppliers] = useState<Brand[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  
  // Loading states
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  
  // Error states
  const [errorUsers, setErrorUsers] = useState<string | null>(null);
  const [errorBrands, setErrorBrands] = useState<string | null>(null);
  const [errorCustomers, setErrorCustomers] = useState<string | null>(null);
  const [errorPermissions, setErrorPermissions] = useState<string | null>(null);

  // Refresh users for dropdowns (accessible to all authenticated users, minimal data)
  const refreshUsersForDropdown = async () => {
    // Prevent duplicate concurrent calls
    if (loadingUsers) {
      return;
    }
    try {
      setLoadingUsers(true);
      setErrorUsers(null);
      // Use dropdown endpoint which returns minimal data and is accessible to all
      const data = await userService.getDropdownList({ status: 'active' });
      setUsers(data);
    } catch (err: any) {
      setErrorUsers(err.response?.data?.message || 'Failed to load users');
      console.error('Failed to refresh users for dropdown:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Refresh users (full data, only if user has permission)
  const refreshUsers = async () => {
    // Org admin always has access to full data
    if (user?.role === 'org_admin') {
      // Prevent duplicate concurrent calls
      if (loadingUsers) {
        return;
      }
      try {
        setLoadingUsers(true);
        setErrorUsers(null);
        const data = await userService.getAll();
        setUsers(data);
      } catch (err: any) {
        setErrorUsers(err.response?.data?.message || 'Failed to load users');
        console.error('Failed to refresh users:', err);
      } finally {
        setLoadingUsers(false);
      }
    } else if (permissions) {
      // Check if user has permission to view users
      const hasPermission = permissionService.hasPermission(
        permissions.permissions,
        'users',
        'view'
      );
      if (hasPermission) {
        // User has permission, load full data
        if (loadingUsers) {
          return;
        }
        try {
          setLoadingUsers(true);
          setErrorUsers(null);
          const data = await userService.getAll();
          setUsers(data);
        } catch (err: any) {
          if (err.response?.status === 403) {
            // Fallback to dropdown if permission denied
            await refreshUsersForDropdown();
          } else {
            setErrorUsers(err.response?.data?.message || 'Failed to load users');
            console.error('Failed to refresh users:', err);
          }
        } finally {
          setLoadingUsers(false);
        }
      } else {
        // User doesn't have permission, use dropdown endpoint for minimal data
        await refreshUsersForDropdown();
      }
    } else {
      // Permissions not loaded yet, use dropdown endpoint (safe fallback)
      await refreshUsersForDropdown();
    }
  };

  // Refresh brands (non-supplier only: is_supplier=0)
  const refreshBrands = async () => {
    try {
      setLoadingBrands(true);
      setErrorBrands(null);
      const data = await brandService.getAll({ is_supplier: false });
      setBrands(data);
    } catch (err: any) {
      setErrorBrands(err.response?.data?.message || 'Failed to load brands');
      console.error('Failed to refresh brands:', err);
    } finally {
      setLoadingBrands(false);
    }
  };

  // Refresh suppliers (brands where is_supplier=1)
  const refreshSuppliers = async () => {
    try {
      const data = await brandService.getAll({ is_supplier: true });
      setSuppliers(data);
    } catch (err: any) {
      console.error('Failed to refresh suppliers:', err);
    }
  };

  // Refresh customers
  const refreshCustomers = async () => {
    try {
      setLoadingCustomers(true);
      setErrorCustomers(null);
      const response = await customerService.getAll();
      setCustomers(response.data || []);
    } catch (err: any) {
      setErrorCustomers(err.response?.data?.message || 'Failed to load customers');
      console.error('Failed to refresh customers:', err);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Refresh permissions
  const refreshPermissions = async (): Promise<UserPermissions | null> => {
    try {
      setLoadingPermissions(true);
      setErrorPermissions(null);
      const data = await permissionService.getMyPermissions();
      setPermissions(data);
      setUserPermissions(data); // Store for permission checks
      return data;
    } catch (err: any) {
      setErrorPermissions(err.response?.data?.message || 'Failed to load permissions');
      console.error('Failed to refresh permissions:', err);
      return null;
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Refresh all data (only what user has permission for)
  const refreshAll = async () => {
    // Always refresh permissions first to check what user can access
    const loadedPermissions = await refreshPermissions();
    
    // Then refresh other data based on permissions
    const refreshPromises = [
      refreshBrands(),
      refreshSuppliers(),
      refreshCustomers()
    ];
    
    // Always load users - use full data if user has permission, otherwise use dropdown data
    // This ensures dropdowns always work
    refreshPromises.push(refreshUsers());
    
    await Promise.all(refreshPromises);
  };

  // Clear all data
  const clearData = () => {
    setUsers([]);
    setBrands([]);
    setSuppliers([]);
    setCustomers([]);
    setPermissions(null);
    setErrorUsers(null);
    setErrorBrands(null);
    setErrorCustomers(null);
    setErrorPermissions(null);
  };

  // Initial load on login
  useEffect(() => {
    const currentUserId = user?.id?.toString() || null;
    
    // Prevent duplicate calls from React StrictMode
    // Only refresh if user changed or not yet initialized
    if (isAuthenticated && user) {
      if (!hasInitializedRef.current || lastUserRef.current !== currentUserId) {
        hasInitializedRef.current = true;
        lastUserRef.current = currentUserId;
        refreshAll();
      }
    } else {
      hasInitializedRef.current = false;
      lastUserRef.current = null;
      clearData();
    }
  }, [isAuthenticated, user]);

  return (
    <AppDataContext.Provider
      value={{
        users,
        brands,
        suppliers,
        customers,
        permissions,
        loadingUsers,
        loadingBrands,
        loadingCustomers,
        loadingPermissions,
        errorUsers,
        errorBrands,
        errorCustomers,
        errorPermissions,
        refreshUsers,
        refreshUsersForDropdown,
        refreshBrands,
        refreshSuppliers,
        refreshCustomers,
        refreshPermissions,
        refreshAll,
        clearData,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};



